import { Card, Col, Input, message, notification, Row, Select, Space, Table, Tag } from 'antd'
import { Button, ButtonActionBar, Typography } from 'modules/core/components'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useEffect, useState } from 'react'
import { EyeOutlined, FolderViewOutlined, DownloadOutlined } from '@ant-design/icons'
import { generateMessageError, generateSelectOptions, sendUserDataToGTM } from 'modules/core/utils'
import { ActionConfirmModal, ChartsSac } from './components'
import _ from 'lodash'
import login from 'modules/login'
import planning from 'modules/planning'
import numeral from 'numeral'
import moment from 'moment'
import configuration from 'modules/configuration'
import { useParams } from 'react-router-dom'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import './GlobalBudgetSACList.scss'

const { Search } = Input

const GlobalBudgetSACList = ({
  isKpisLoading,
  adjustmentsKpis,
  adjustmentsList,
  onClickDetail,
  onClickHistorical,
  onClickCreate,
  onRefetch,
  onRefetchKpis,
  activeStatusFilter,
  activeSearchId,
  isMyPending,
  activeSocietyIds,
  activeAdjustmentTypes,
  societyOptions,
  areaList,
  fetchUsersList,
  usersList,
  loggedUser: { pkid },
  approveRevieweRejectedApplication,
  rejectedApplication,
  dataLineChartSac,
  dataPieChartSac,
  fetchDataLineChartSAC,
  fetchDataPieChartSAC,
  fetchDataTableAreaSAC,
  dataTablaAreasSac,
  loggedUser,
  downloadSacListXLS,
}) => {
  const enableCharts = useFeatureIsOn('ab_sac_graphics')
  const enableMiniTable = useFeatureIsOn('ab_sac_mini_table')
  const [isSaving, setIsSaving] = useState(false)
  const [dataRow, setDataRow] = useState({})
  const [idStep, setIdStep] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmAction, setConfirmAction] = useState('approve')
  const [searchValue, setSearchValue] = useState('')
  const { t } = useTranslation()
  let { globalBudgetId } = useParams()

  const canUserViewTable = loggedUser.roles.find((el) => el.code === 'adm' && el.name === 'Admin')

  useEffect(() => {
    if (canUserViewTable) {
      fetchDataTableAreaSAC({ projection: globalBudgetId })
    }
  }, [fetchDataTableAreaSAC, globalBudgetId, canUserViewTable])

  useEffect(() => {
    fetchUsersList()
  }, [fetchUsersList])

  useEffect(() => {
    Promise.all([
      fetchDataLineChartSAC({ overall_budget_id: globalBudgetId }),
      fetchDataPieChartSAC({ projection: globalBudgetId, module: 'OVERALL_PLANNING' }),
    ])
  }, [fetchDataLineChartSAC, globalBudgetId, fetchDataPieChartSAC])

  const getStatus = (completed) => {
    return {
      requested: completed ? 'STATUS_REQUESTED' : 'ACTION_REQUEST',
      reviewed_ordinary_area: completed ? 'STATUS_REVIEWED' : 'ACTION_REVIEW',
      approved_ordinary_area: completed ? 'STATUS_DATE_APPROVE' : 'ACTION_APPROVE',
      rejected_ordinary_area: completed ? 'STATUS_REQUEST_REJECTED' : 'ACTION_REJECTED',
    }
  }

  const ADJUSTMENTS_TYPE = {
    EXPANSION: 'LABEL_EXPANSION',
    REDUCTION: 'LABEL_REDUCTION',
    RECLASSIFICATION: 'LABEL_RECLASSIFICATION',
  }

  const ADJUSTMENT_TYPE_OPTIONS = Object.entries(ADJUSTMENTS_TYPE).map(([id, label]) => ({
    id,
    name: t(label),
  }))

  const STATUS = {
    pending_approval: 'STATUS_PENDING_APPROVE',
    pending_review: 'STATUS_PENDING_REVIEW',
    rejected_ordinary_area: 'STATUS_REJECTED',
    approved_ordinary_area: 'STATUS_APPROVED',
    total: 'FIELD_TOTAL',
  }

  const COLOR_STATUS = {
    requested: 'gold',
    reviewed_ordinary_area: 'processing',
    approved_ordinary_area: 'success',
    rejected_ordinary_area: 'error',
    bypassed_ordinary_area: 'default',
  }

  const handleApproveReviewApplication = (id, comment) => {
    setIsSaving(true)
    const payload = { action: 'approve', ...(comment ? { comment } : {}) }
    approveRevieweRejectedApplication(id, payload)
      .then(() => {
        Promise.all([
          fetchDataLineChartSAC({ overall_budget_id: globalBudgetId }),
          fetchDataPieChartSAC({ projection: globalBudgetId, module: 'OVERALL_PLANNING' }),
          onRefetch(),
          onRefetchKpis(),
          fetchDataTableAreaSAC({ projection: globalBudgetId }),
        ]).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDataRow({})
          setShowConfirmModal(false)
          sendUserDataToGTM(loggedUser, 'SAC', `Aprobar revisar SAC`, `Aprobacion/revisión`)
        })
      })
      .catch((error) => {
        setIsSaving(false)
        setDataRow({})
        setShowConfirmModal(false)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const handleRejectedApplication = (id, comment) => {
    setIsSaving(true)
    const payload = { action: 'reject', ...(comment ? { comment } : {}) }
    approveRevieweRejectedApplication(id, payload)
      .then(() => {
        Promise.all([
          fetchDataTableAreaSAC({ projection: globalBudgetId }),
          onRefetch(),
          onRefetchKpis(),
        ]).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDataRow({})
          setShowConfirmModal(false)
          sendUserDataToGTM(loggedUser, 'SAC', `Rechazar SAC`, `Rechazo`)
        })
      })
      .catch((error) => {
        setIsSaving(false)
        setDataRow({})
        setShowConfirmModal(false)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const handleConfirmAction = (comment) => {
    if (confirmAction === 'reject') {
      handleRejectedApplication(idStep, comment)
    } else {
      handleApproveReviewApplication(idStep, comment)
    }
  }

  const renderTitle = (step) => {
    if (!step) return ''
    return t(getStatus(step.completed)[step.status])
  }

  const getCurrentLevelUserStep = (statuses = []) => {
    const sortedStatuses = getSortedStatuses(statuses)
    const pendingSteps = sortedStatuses.filter((el) => !el.completed)
    if (pendingSteps.length === 0) return null
    const currentLevelPosition = pendingSteps[0].position
    return (
      pendingSteps.find((el) => el.position === currentLevelPosition && el.responsible === pkid) ||
      null
    )
  }

  const getSortedStatuses = (statuses = []) => {
    return [...statuses].sort((itemA, itemB) => itemA.position - itemB.position)
  }

  const getCurrentStatus = (statuses = []) => {
    const sortedStatuses = getSortedStatuses(statuses).filter(
      (status) => status.status !== 'bypassed_ordinary_area',
    )
    const completedStatuses = sortedStatuses.filter((status) => status.completed)
    return (
      completedStatuses[completedStatuses.length - 1] ||
      sortedStatuses.find((status) => !status.completed) ||
      sortedStatuses[sortedStatuses.length - 1] ||
      null
    )
  }

  const columns = [
    {
      title: 'N°',
      dataIndex: 'id',
    },
    {
      title: t('LABEL_USER'),
      dataIndex: 'user',
      render: (user) => usersList.values.find((el) => el.id === user)?.full_name,
    },
    {
      title: 'SOCIEDAD',
      dataIndex: 'society_name',
    },
    {
      title: t('FIELD_DATE'),
      dataIndex: 'created_at',
      render: (date) => (
        <Space direction="vertical" size={0}>
          <Typography.Body level={3}>{moment(date).format('DD/MM/YYYY')}</Typography.Body>

          <div
            style={{
              color: '#8c8c8c',
              fontSize: 11,
            }}
          >
            {moment(date).fromNow()}
          </div>
        </Space>
      ),
    },
    {
      title: t('FIELD_TYPE'),
      dataIndex: 'adjustment_type',
      render: (type) => t(ADJUSTMENTS_TYPE[type]),
    },
    {
      title: t('FIELD_OBSERVATIONS'),
      dataIndex: 'description',
    },
    {
      title: t('FIELD_AMOUNT'),
      dataIndex: 'total_amount',
      render: (amount) => numeral(amount).format('0,0.00'),
    },
    {
      title: t('FIELD_STATUS'),
      dataIndex: 'statuses',
      render: (statuses) => {
        const currentStatus = getCurrentStatus(statuses)
        if (!currentStatus) return null

        return (
          <Tag
            color={COLOR_STATUS[currentStatus.status]}
            style={{
              borderRadius: 16,
              padding: '2px 12px',
              fontWeight: 500,
              minWidth: 90,
              textAlign: 'center',
            }}
          >
            {t(getStatus(currentStatus.completed)[currentStatus.status])}
          </Tag>
        )
      },
    },
    {
      title: t('FIELD_ACTIONS'),
      dataIndex: 'id',
      align: 'right',
      render: (id, record) => {
        const userStep = getCurrentLevelUserStep(record.statuses)
        const stepId = userStep?.id ?? null

        return (
          <Space direction="horizontal">
            {userStep && (
              <>
                <Button.Default
                  loading={isSaving && id === dataRow.id}
                  onClick={() => {
                    setShowConfirmModal(true)
                    setDataRow(record)
                    setIdStep(stepId)
                    setConfirmAction('reject')
                  }}
                  title="ACTION_REJECTED"
                />
                <Button.Primary
                  onClick={() => {
                    setShowConfirmModal(true)
                    setDataRow(record)
                    setIdStep(stepId)
                    setConfirmAction('approve')
                  }}
                  title={renderTitle(userStep)}
                />
              </>
            )}
            <Button.Icon
              type="text"
              shape="circle"
              icon={<Typography.Icon icon={EyeOutlined} />}
              title={t('ACTION_OPEN_DETAIL')}
              onClick={() => onClickDetail(id)}
            />
            <Button.Icon
              type="text"
              shape="circle"
              icon={<Typography.Icon icon={FolderViewOutlined} />}
              title={t('ACTION_OPEN_DETAIL')}
              onClick={() => onClickHistorical(id)}
            />
          </Space>
        )
      },
    },
  ]

  const kpisTotal = Object.values(adjustmentsKpis).reduce(
    (sum, kpi) => sum + (typeof kpi === 'number' ? kpi : kpi.quantity || 0),
    0,
  )

  const renderKpis = () => {
    if (isKpisLoading) {
      return (
        <Row gutter={[24, 0]}>
          {[1, 2, 3, 4, 5].map((index) => (
            <Col span={6} key={index}>
              <Card loading={true} />
            </Col>
          ))}
        </Row>
      )
    }
    if (!isKpisLoading) {
      return (
        <Row gutter={[24, 0]}>
          {_.map({ total: kpisTotal, ...adjustmentsKpis }, (data, key) => {
            return (
              <Col xs={24} sm={12} md={8} lg={4} key={key}>
                <Card
                  hoverable
                  onClick={() => onRefetch(key === 'total' ? null : key)}
                  className="sac-kpi-card"
                  style={{
                    cursor: 'pointer',
                    borderRadius: 14,
                    border:
                      (key === 'total' && !activeStatusFilter) || activeStatusFilter === key
                        ? '2px solid #1677ff'
                        : '1px solid #f0f0f0',
                    boxShadow:
                      (key === 'total' && !activeStatusFilter) || activeStatusFilter === key
                        ? '0 4px 14px rgba(22,119,255,0.15)'
                        : '0 2px 10px rgba(0,0,0,0.05)',
                  }}
                >
                  {typeof data === 'number' ? (
                    <Row gutter={[4, 4]}>
                      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space direction="vertical">
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Typography.Headline level={4}>{data}</Typography.Headline>
                          </div>
                          <Typography.Body level={3}>{t(STATUS[key])}</Typography.Body>
                        </Space>
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={[24, 24]}>
                      <Col span={10} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space direction="vertical">
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Typography.Headline level={4}>{data.quantity}</Typography.Headline>
                          </div>
                          <Typography.Body level={3}>{t(STATUS[key])}</Typography.Body>
                        </Space>
                      </Col>
                      <Col span={14} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space direction="vertical">
                          <Typography.Headline level={5}>
                            ${numeral(data.total_amount).format('0,0a')}
                          </Typography.Headline>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Typography.Body level={2}>{t('FIELD_TOTAL')}</Typography.Body>
                          </div>
                        </Space>
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            )
          })}
        </Row>
      )
    }
  }

  const columnsAreas = [
    {
      title: t('LABEL_AREA'),
      dataIndex: 'area',
    },
    {
      title: t('STATUS_PENDING_APPROVE'),
      dataIndex: 'pending_approval',
    },
    {
      title: t('STATUS_PENDING_REVIEW'),
      dataIndex: 'pending_review',
    },
    {
      title: t('STATUS_REJECTED'),
      dataIndex: 'rejected_ordinary_area',
    },
    {
      title: t('STATUS_APPROVED'),
      dataIndex: 'approved_ordinary_area',
    },
    {
      title: t('FIELD_TOTAL'),
      dataIndex: 'total',
    },
  ]

  const generateDataSorce = () =>
    _.reduce(
      dataTablaAreasSac,
      (acum, data, key) => {
        return [...acum, { ...data, area: key }]
      },
      [],
    )

  return (
    <>
      <Col span={24}>{renderKpis()}</Col>
      <Col span={24}>
        <Card
          className="sac-filter-card"
          style={{ marginBottom: 12 }}
          bodyStyle={{
            padding: 16,
          }}
        >
          <ButtonActionBar position="space-between">
            <Space wrap>
              <Search
                placeholder="Número de solicitud"
                onSearch={(value) => onRefetch(activeStatusFilter, value)}
                allowClear
                style={{ width: 250 }}
              />
              <Select
                mode="multiple"
                allowClear
                placeholder={t('LABEL_COMPANY')}
                value={activeSocietyIds}
                onChange={(vals) =>
                  onRefetch(activeStatusFilter, activeSearchId, isMyPending || undefined, vals)
                }
                options={generateSelectOptions({ options: societyOptions })}
                style={{ minWidth: 200 }}
                maxTagCount="responsive"
              />
              <Select
                mode="multiple"
                allowClear
                placeholder={t('FIELD_TYPE')}
                value={activeAdjustmentTypes}
                onChange={(vals) =>
                  onRefetch(
                    activeStatusFilter,
                    activeSearchId,
                    isMyPending || undefined,
                    undefined,
                    vals,
                  )
                }
                options={generateSelectOptions({ options: ADJUSTMENT_TYPE_OPTIONS })}
                style={{ minWidth: 200 }}
                maxTagCount="responsive"
              />
            </Space>
            <Space>
              <Button.Default
                title="PLANNING_SAC_MY_PENDING"
                onClick={() => onRefetch(null, null, !isMyPending || undefined)}
                type={isMyPending ? 'primary' : 'default'}
              />
              <Button.Primary
                disabled={areaList.length === 0}
                title="ACTION_CREATE_NEW"
                onClick={() => onClickCreate()}
              />
            </Space>
          </ButtonActionBar>
        </Card>
        <ButtonActionBar>
          <Button.Icon
            title="ADJUSTMENT_DOWNLOAD_MOVEMENT_ACTION_TITLE"
            icon={<Typography.Icon icon={DownloadOutlined} />}
            onClick={() =>
              downloadSacListXLS({
                projection: globalBudgetId,
                module: 'OVERALL_PLANNING',
                ...(isMyPending && {
                  status: 'pending_approval',
                }),
                ...(activeAdjustmentTypes.length > 0 && {
                  adjustment_type: activeAdjustmentTypes.join(','),
                }),
              })
            }
          />
        </ButtonActionBar>
        <Table
          className="sac-table"
          style={{ marginTop: 12 }}
          size="middle"
          columns={columns}
          dataSource={adjustmentsList}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            // showTotal: total => `${total} solicitudes`,
          }}
          rowClassName={(record) => {
            const currentStatus = getCurrentStatus(record.statuses)

            if (currentStatus?.status === 'requested') {
              return 'row-pending'
            }

            if (currentStatus?.status === 'approved_ordinary_area') {
              return 'row-approved'
            }

            return ''
          }}
        />
      </Col>
      {enableCharts && (
        <ChartsSac dataLineChartSac={dataLineChartSac} dataPieChartSac={dataPieChartSac} />
      )}
      {enableMiniTable && canUserViewTable && (
        <Col span={12}>
          <Card title={t('PLANNING_SAC_TABLE_TITLE')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <ButtonActionBar>
                <Search
                  placeholder={t('ACTION_SEARCH')}
                  onSearch={setSearchValue}
                  style={{ width: 200 }}
                />
              </ButtonActionBar>
              <Table
                size="small"
                columns={columnsAreas}
                dataSource={
                  searchValue
                    ? generateDataSorce().filter((el) =>
                        el.area.toLowerCase().includes(searchValue.toLowerCase()),
                      )
                    : generateDataSorce()
                }
                pagination={false}
              />
            </Space>
          </Card>
        </Col>
      )}
      <ActionConfirmModal
        visible={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false)
          setDataRow({})
        }}
        onConfirm={handleConfirmAction}
        isSaving={isSaving}
        dataRow={dataRow}
        usersList={usersList}
      />
    </>
  )
}

const mapStateToProps = (state) => ({
  usersList: configuration.selectors.getUsersList(state),
  loggedUser: login.selectors.getWhoAmI(state),
  dataLineChartSac: planning.selectors.getDataLineChartSac(state),
  dataPieChartSac: planning.selectors.getDataPieChartSac(state),
  dataTablaAreasSac: planning.selectors.getDataTablaAreasSac(state),
})

const mapDispatchToProps = {
  fetchUsersList: configuration.actions.fetchUsersList,
  approveRevieweRejectedApplication: planning.actions.approveRevieweRejectedApplication,
  rejectedApplication: planning.actions.rejectedApplication,
  fetchDataLineChartSAC: planning.actions.fetchDataLineChartSAC,
  fetchDataPieChartSAC: planning.actions.fetchDataPieChartSAC,
  fetchDataTableAreaSAC: planning.actions.fetchDataTableAreaSAC,
  downloadSacListXLS: planning.actions.downloadSacListXLS,
}

export default connect(mapStateToProps, mapDispatchToProps)(GlobalBudgetSACList)
